import { Link, useLocation } from 'react-router-dom'
import { Calendar, MapPin, User, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import { getTalentTypeLabel, getTalentTypeColor } from '../../utils/talentTypeUtils'
import { getStatusStyles } from '../../utils/colorUtils'
import { getToolIcon } from '../../utils/toolIconUtils.jsx'

const RecentTalents = ({ talents = [], loading = false }) => {
  const location = useLocation()
  if (loading) {
    return (
      <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div className="flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
                    <div className="h-5 w-20 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
              </div>
              <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded-md" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!talents || talents.length === 0) {
    return (
      <div className="p-6 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
        <User className="h-12 w-12 mx-auto mb-3 text-light-text-tertiary dark:text-dark-text-tertiary" />
        <p className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">No data</p>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No talents have been added to the system yet</p>
      </div>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified'

    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'

    // If status has color, use utility to create styles
    if (status.color) {
      const styles = getStatusStyles(status.color, {
        backgroundColorOpacity: 0.1,
        borderOpacity: 0.3
      })
      return styles
    }

    // Default gray color for dark theme support
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }

  const getStatusLabel = (status) => {
    return status?.name || 'Not defined'
  }


  // Функция копирования контакта в буфер обмена
  const copyContactToClipboard = async (contactValue) => {
    try {
      await navigator.clipboard.writeText(contactValue);
      toast.success(`Copied: ${contactValue}`, {
        duration: 2000,
        position: 'bottom-right',
      });
    } catch (err) {
      console.error('Failed to copy contact:', err);
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = contactValue;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(`Copied: ${contactValue}`, {
          duration: 2000,
          position: 'bottom-right',
        });
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        toast.error('Failed to copy contact', {
          duration: 2000,
          position: 'bottom-right',
        });
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
      {talents.map((talent) => (
        <div key={talent.id} className="p-4 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors sm:p-6">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>

              {/* Talent Information */}
              <div className="flex-1 min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2 sm:gap-3">
                  <h4 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                  {talent.last_name} {talent.first_name}
                  </h4>
                  
                  {/* Статус */}
                  <span 
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      typeof getStatusColor(talent.currentStatus) === 'string' 
                        ? getStatusColor(talent.currentStatus) 
                        : ''
                    }`}
                    style={typeof getStatusColor(talent.currentStatus) === 'object' 
                      ? getStatusColor(talent.currentStatus) 
                      : undefined
                    }
                  >
                    {getStatusLabel(talent.currentStatus)}
                  </span>
                  
                  {/* Тип таланта */}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTalentTypeColor(talent.talent_type)}`}>
                    {getTalentTypeLabel(talent.talent_type)}
                  </span>
                </div>

                {/* Additional Information */}
                <div className="flex flex-wrap items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary sm:gap-3">
                  {talent.contacts && talent.contacts.length > 0 ? (
                    <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                      {talent.contacts.slice(0, 2).map((contact, index) => {
                        const contactValue = typeof contact.value === 'object' ? contact.value?.value || 'Not specified' : contact.value || 'Not specified';

                        return (
                          <div key={contact.id} className="flex items-center space-x-2 min-w-0">
                            {contact.tool && (
                              <span
                                className="inline-flex items-center cursor-help"
                                title={contact.tool.name}
                              >
                                {getToolIcon(contact.tool, { size: 'h-4 w-4' })}
                              </span>
                            )}
                            <div className="flex-1 min-w-0 flex items-center space-x-1">
                              <div
                                className="text-sm text-light-text-primary dark:text-dark-text-primary truncate max-w-32 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                onClick={() => copyContactToClipboard(contactValue)}
                                title={`Click to copy: ${contactValue}`}
                              >
                                {contactValue}
                              </div>
                              {contact.is_primary && (
                                <span className="ml-1 px-1 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded flex-shrink-0">
                                  Primary
                                </span>
                              )}
                              {contact.is_verified && (
                                <div className="text-green-500 dark:text-green-400 text-xs flex-shrink-0">✓</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {talent.contacts.length > 2 && (
                        <div className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                          +{talent.contacts.length - 2} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No contacts</span>
                  )}
                  
                  {(talent.country || talent.city) && (
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>
                        {talent.country?.name}
                        {talent.city && talent.country && ', '}
                        {talent.city?.name}
                      </span>
                    </div>
                  )}
                  
                  {talent.created_at && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>Added {formatDate(talent.created_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center sm:shrink-0">
              <Link
                to={`/talents/${talent.id}`}
                state={{ from: location.pathname + location.search }}
                className="inline-flex w-full items-center justify-center rounded-md border border-light-border-secondary bg-light-bg-primary px-3 py-2 text-sm font-medium leading-4 text-light-text-primary shadow-sm transition-colors hover:bg-light-bg-secondary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-dark-border-secondary dark:bg-dark-bg-primary dark:text-dark-text-primary dark:hover:bg-dark-bg-secondary sm:w-auto"
              >
                <Eye className="h-4 w-4 mr-1" />
                View
              </Link>
            </div>
          </div>
        </div>
      ))}
      
      {/* Link to all talents */}
      {talents.length > 0 && (
        <div className="p-6 bg-light-bg-secondary dark:bg-dark-bg-secondary">
          <Link
            to="/talents"
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 flex items-center justify-center transition-colors"
          >
            View all talents
            <span className="ml-1">→</span>
          </Link>
        </div>
      )}
    </div>
  )
}

export default RecentTalents
